function normalizeSchedulingText(value = "") {
  return String(value || "")
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

const SCHEDULING_INTENT_PATTERN = /\b(?:please\s+)?(?:remind\s+me|set\s+(?:a\s+)?reminder|schedule(?:\s+(?:a|the|my))?\s+(?:follow[-\s]?up|check[-\s]?in|reminder)|reschedule|move\s+(?:the|my)\s+(?:follow[-\s]?up|check[-\s]?in|reminder)|follow[-\s]?up|check[-\s]?in)\b/i;
const AVAILABILITY_ACTION_PATTERN = /\b(?:return|attend|join|log\s*in|sign\s*in|come\s+back|be\s+(?:available|free)|make\s+(?:it|the\s+(?:meeting|call|session|appointment))|respond|reply|continue|resume|revisit|reconnect|meet\s+with)\b/i;
const AVAILABILITY_STATE_PATTERN = /\b(?:can(?:not|'t)?|won't|will\s+not|not\s+be\s+able|unable|unavailable|not\s+available|available|free|will|plan(?:ning)?\s+to|intend(?:ing)?\s+to)\b/i;
const TEMPORAL_VALUE_SOURCE = "(?:today|tomorrow|tonight|this\\s+(?:morning|afternoon|evening|week|weekend)|next\\s+(?:week|weekend|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|monday|tuesday|wednesday|thursday|friday|saturday|sunday|(?:the\\s+)?weekend|\\d{1,2}(?::|\\.)\\d{2}|\\d{1,2}\\s*(?:am|pm|a\\.m\\.|p\\.m\\.)|\\d{1,2}(?:st|nd|rd|th)?\\s+(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?))";
const TEMPORAL_CONTEXT_PATTERN = new RegExp(
  `(?:\\b(?:until|before|after|from|by|on|at)\\s+(?:${TEMPORAL_VALUE_SOURCE})\\b|\\b${TEMPORAL_VALUE_SOURCE}\\b)`,
  "i"
);
const BARE_HOUR_BOUNDARY_PATTERN = /\b(?:at|until|before|after|from|by)\s+\d{1,2}\b(?!\s*%)/i;

function splitSentences(value = "") {
  const text = normalizeSchedulingText(value);
  if (!text) return [];
  return text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => normalizeSchedulingText(sentence))
    .filter(Boolean);
}

function splitClauses(value = "") {
  return splitSentences(value).flatMap((sentence) => (
    sentence
      .split(/\s*(?:;|,\s*but\s+|,\s*and\s+(?=(?:i|we|please|then)\b)|,\s*(?=(?:please\s+remind|remind|schedule|reschedule)\b)|\s+\bbut\b\s+|\s+\band\b\s+(?=(?:i|we|please|then)\b))/i)
      .map((clause) => normalizeSchedulingText(clause))
      .filter(Boolean)
  ));
}

function isLogisticalAvailabilityClause(value = "") {
  const text = normalizeSchedulingText(value);
  if (!text) return false;
  return AVAILABILITY_ACTION_PATTERN.test(text) &&
    AVAILABILITY_STATE_PATTERN.test(text) &&
    (TEMPORAL_CONTEXT_PATTERN.test(text) || BARE_HOUR_BOUNDARY_PATTERN.test(text));
}

function isLogisticalAvailabilityStatement(value = "") {
  return splitClauses(value).some((clause) => isLogisticalAvailabilityClause(clause));
}

function isSchedulingMessage(value = "") {
  const text = normalizeSchedulingText(value);
  if (!text) return false;
  if (isLogisticalAvailabilityStatement(text)) return true;
  return SCHEDULING_INTENT_PATTERN.test(text) && TEMPORAL_CONTEXT_PATTERN.test(text);
}

function messageText(entry = {}) {
  return normalizeSchedulingText(entry?.content || entry?.text || "");
}

function messageRole(entry = {}) {
  const role = String(entry?.role || entry?.sender || "").toLowerCase();
  if (role === "assistant" || role === "nia") return "assistant";
  return "user";
}

function isSchedulingOnlySession(messages = []) {
  const userMessages = (Array.isArray(messages) ? messages : [])
    .filter((entry) => messageRole(entry) === "user")
    .map((entry) => messageText(entry))
    .filter(Boolean);
  return userMessages.length > 0 && userMessages.every((text) => (
    isSchedulingMessage(text) && isLightweightSchedulingResidue(schedulingResidue(text))
  ));
}

function schedulingResidue(value = "") {
  return normalizeSchedulingText(
    splitClauses(value)
      .filter((clause) => !isLogisticalAvailabilityClause(clause))
      .filter((clause) => !SCHEDULING_INTENT_PATTERN.test(clause))
      .join(" ")
  );
}

function isLightweightSchedulingResidue(value = "") {
  const clauses = splitClauses(value);
  if (!clauses.length) return true;
  const lightweightPatterns = [
    /^(?:(?:my|the)\s+)?plans?\s+(?:have\s+)?changed\b/i,
    /^(?:my\s+)?circumstances?\s+(?:have\s+)?changed\b/i,
    /^i\s+(?:need|want|plan|intend|will|have)\s+(?:to\s+)?(?:finish|complete|do|send|submit|handle|attend|join|return|log\s*in|remember)\b/i,
    /^i\s+(?:have|need)\s+(?:an?|the)\s+(?:important\s+)?(?:task|appointment|meeting|call|deadline)\b/i,
    /^(?:my|the|this)\s+(?:task|appointment|meeting|call|deadline)\b.{0,100}\b(?:today|tomorrow|tonight|monday|tuesday|wednesday|thursday|friday|saturday|sunday|due)\b/i,
    /^(?:please\s+)?(?:do\s+not|don't)\s+let\s+me\s+forget\b/i,
  ];
  return clauses.every((clause) => lightweightPatterns.some((pattern) => pattern.test(clause)));
}

function stripLogisticalAvailabilityStatements(value = "") {
  const text = normalizeSchedulingText(value);
  if (!text) return "";
  return normalizeSchedulingText(
    splitClauses(text)
      .filter((clause) => !isLogisticalAvailabilityClause(clause))
      .join(" ")
  );
}

exports.isLogisticalAvailabilityStatement = isLogisticalAvailabilityStatement;
exports.isSchedulingMessage = isSchedulingMessage;
exports.isSchedulingOnlySession = isSchedulingOnlySession;
exports.normalizeSchedulingText = normalizeSchedulingText;
exports.stripLogisticalAvailabilityStatements = stripLogisticalAvailabilityStatements;
